import React, { useState, useEffect } from "react"
import {Calendar, MapPin, Plus, Edit2, Trash2, X, Save, Users} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface Event {
  _id: string
  date: string
  startTime: string
  endTime: string
  title: string
  location: string
  description: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface EventsManagerProps {
  onClose: () => void
  embedded?: boolean
}

export function EventsManager({ onClose, embedded = false }: EventsManagerProps) {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddingEvent, setIsAddingEvent] = useState(false)
  const [editingEventId, setEditingEventId] = useState<string | null>(null)
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] = useState<string | null>(null)
  const [registrations, setRegistrations] = useState<any[]>([])
  const [loadingRegistrations, setLoadingRegistrations] = useState(false)
  const [formData, setFormData] = useState({
    date: "",
    startTime: "",
    endTime: "",
    title: "",
    location: "",
    description: ""
  })

  const filterAndSortEvents = (eventsList: Event[]) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return eventsList
      .filter(event => {
        const eventDate = new Date(event.date)
        return eventDate >= now
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.events.list({
        sort: { createdAt: -1 }
      })
      const filteredEvents = filterAndSortEvents(result.list || [])
      setEvents(filteredEvents)
    } catch (error) {
      console.error("Erreur lors du chargement des événements:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchRegistrations = async (eventId: string) => {
    setLoadingRegistrations(true)
    try {
      const result = await lumi.entities.event_registrations.list({
        filter: { eventId },
        sort: { createdAt: -1 }
      })
      setRegistrations(result.list || [])
    } catch (error) {
      console.error("Erreur lors du chargement des inscriptions:", error)
      setRegistrations([])
    } finally {
      setLoadingRegistrations(false)
    }
  }

  const viewRegistrations = (eventId: string) => {
    setSelectedEventForRegistrations(eventId)
    fetchRegistrations(eventId)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const user = lumi.auth.user
    if (!user) {
      alert("Vous devez être connecté")
      return
    }

    try {
      const eventData = {
        ...formData,
        creator: user.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      if (editingEventId) {
        await lumi.entities.events.update(editingEventId, {
          ...formData,
          updatedAt: new Date().toISOString()
        })
      } else {
        await lumi.entities.events.create(eventData)
      }

      setFormData({ date: "", startTime: "", endTime: "", title: "", location: "", description: "" })
      setIsAddingEvent(false)
      setEditingEventId(null)
      await fetchEvents()
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error)
      alert("Erreur lors de l'enregistrement de l'événement")
    }
  }

  const handleEdit = (event: Event) => {
    setFormData({
      date: event.date,
      startTime: event.startTime,
      endTime: event.endTime,
      title: event.title,
      location: event.location,
      description: event.description
    })
    setEditingEventId(event._id)
    setIsAddingEvent(true)
  }

  const handleDelete = async (eventId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) {
      return
    }

    try {
      await lumi.entities.events.delete(eventId)
      await fetchEvents()
    } catch (error) {
      console.error("Erreur lors de la suppression:", error)
      alert("Erreur lors de la suppression de l'événement")
    }
  }

  const handleCancel = () => {
    setFormData({ date: "", startTime: "", endTime: "", title: "", location: "", description: "" })
    setIsAddingEvent(false)
    setEditingEventId(null)
  }

  const content = (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Événements</h2>
          <p className="text-gray-600 mt-1">Gérez les événements et conférences RUGIR</p>
        </div>
        {!isAddingEvent && (
          <button
            onClick={() => setIsAddingEvent(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            Ajouter un événement
          </button>
        )}
      </div>

      {/* Form for adding/editing event */}
      {isAddingEvent && (
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 mb-6 border-2 border-teal-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {editingEventId ? "Modifier l'événement" : "Nouvel événement"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date de l'événement
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure de début
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Heure de fin
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Titre
              </label>
              <input
                type="text"
                placeholder="Ex: Atelier : Pilotage Stratégique"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Lieu
              </label>
              <input
                type="text"
                placeholder="Ex: Genève, En ligne, etc."
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                placeholder="Décrivez l'événement..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none resize-none"
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                <Save className="w-5 h-5" />
                {editingEventId ? "Mettre à jour" : "Créer l'événement"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-2 bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-all"
              >
                <X className="w-5 h-5" />
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="text-gray-600 mt-4">Chargement des événements...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Aucun événement pour le moment</p>
          <p className="text-gray-500 text-sm mt-2">Cliquez sur "Ajouter un événement" pour commencer</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div key={event._id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all p-6 border-t-4 border-teal-600">
              <div className="flex items-center gap-2 text-teal-600 mb-3">
                <Calendar className="w-5 h-5" />
                <span className="font-semibold">{new Date(event.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="text-sm text-gray-600 mb-3">
                {event.startTime} - {event.endTime}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
              <div className="flex items-center gap-2 text-gray-600 mb-3">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
              <p className="text-gray-600 mb-4 line-clamp-3">{event.description}</p>
              
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => viewRegistrations(event._id)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold text-sm"
                >
                  <Users className="w-4 h-4" />
                  Inscrits
                </button>
                <button
                  onClick={() => handleEdit(event)}
                  className="flex items-center gap-1 text-teal-600 hover:text-teal-700 font-semibold text-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(event._id)}
                  className="flex items-center gap-1 text-red-600 hover:text-red-700 font-semibold text-sm ml-auto"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      {embedded ? (
        content
      ) : (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-8">
            <button
              onClick={onClose}
              className="float-right p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            {content}
          </div>
        </div>
      )}
      
      {/* Registrations Modal - Always show when needed */}
      {selectedEventForRegistrations && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Liste des inscrits</h2>
                <p className="text-gray-600 mt-1">
                  {events.find(e => e._id === selectedEventForRegistrations)?.title}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedEventForRegistrations(null)
                  setRegistrations([])
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {loadingRegistrations ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                <p className="text-gray-600 mt-4">Chargement des inscriptions...</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">Aucune inscription pour cet événement</p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((registration) => (
                  <div key={registration._id} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">Nom</p>
                        <p className="text-lg font-bold text-gray-900">{registration.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">Entreprise</p>
                        <p className="text-lg text-gray-900">{registration.company}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">Email</p>
                        <p className="text-gray-900">{registration.email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-500 mb-1">Téléphone</p>
                        <p className="text-gray-900">{registration.phone}</p>
                      </div>
                    </div>
                    {registration.message && (
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <p className="text-sm font-semibold text-gray-500 mb-2">Message</p>
                        <p className="text-gray-700">{registration.message}</p>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-gray-300 text-sm text-gray-500">
                      Inscrit le {new Date(registration.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
